import React, { memo, useState, useEffect, useMemo, useContext } from 'react';
import { Table, TableProps } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import { context } from '@gp/components/Context';
import { getColumns } from './utils/common';
import { getNumberOfDotUnit } from '@gp/utils/common';
import { ACTION_ITEM_STATUS_OPTIONS } from '@gp/constants/const';
import { useMemoizedFn } from 'ahooks';
import useActionItemsFilterList from './hooks/useActionItemsFilterList';
import classNames from 'classnames';
import { ContentType } from '../../index.d';
import { queryActionItemsListResultType } from '@gp/types/projectUpdate.d';

import CardSlot from '@/pages/globalpedia/components/CardSlot';

import './index.scss';

const DEFAULT_SELECT_STATUS = ['1', '2', '3'];
const { autotip, sortable, pageable, scrollable } = Table.addons;

interface ActionItemsListPropsType {
  loading: boolean;
  userIsAbleEdit: boolean;
  data: queryActionItemsListResultType | null;
  downloadParams: {
    selectDate: [string, string];
    sorts: SortBy<any>;
    selectStatus: string[];
    selectPhase: string[];
  };
  setDownloadParams: React.Dispatch<
    React.SetStateAction<{
      selectDate: [string, string];
      sorts: SortBy<any>;
      selectStatus: string[];
      selectPhase: string[];
    }>
  > | null;
  hideHeader: boolean;
  onEdit?: (cvm: any) => void;
}

const DEFAULT_PROPS: ActionItemsListPropsType = {
  loading: false,
  userIsAbleEdit: false,
  data: null,
  downloadParams: {
    selectDate: ['', ''],
    sorts: {
      by: 'due_date',
      order: 'asc',
    },
    selectStatus: DEFAULT_SELECT_STATUS,
    selectPhase: [],
  },
  setDownloadParams: null,
  hideHeader: false,
  onEdit: undefined,
};

function ActionItemsList(props: ActionItemsListPropsType) {
  const { loading, userIsAbleEdit, data, downloadParams, setDownloadParams, hideHeader, onEdit } =
    props;

  const { projectDetailData, milestones }: ContentType =
    useContext(context) || {};

  const [dataList, setDataList] = useState<
    queryActionItemsListResultType['workplans'] | null
  >(null);
  // 表格是否出现滚动条
  const [tableIsScroll, setTableIsScroll] = useState<boolean>(false);
  const [tableBodyMaxH, setTableBodyMaxH] = useState<number>(500);
  const [records, setRecords] = useState<
    queryActionItemsListResultType['workplans']
  >([]);
  const [sorts, setSorts] = useState<SortBy<any>>({
    by: 'due_date',
    order: 'asc',
  });
  const [paginationMsg, setPaginationMsg] = useState<{
    pageIndex: number;
    pageSize: number;
    total: number;
  }>({ pageIndex: 1, pageSize: 20, total: 0 });
  const [selectStatus, setSelectStatus] = useState<string[]>(
    DEFAULT_SELECT_STATUS,
  );
  const [lastSelectStatus, setLastSelectStatus] = useState<string[]>(
    DEFAULT_SELECT_STATUS,
  );
  const [selectPhase, setSelectPhase] = useState<string[]>([]);
  const [lastSelectPhase, setLastSelectPhase] = useState<string[]>([]);

  useActionItemsFilterList({
    dataList,
    selectStatus,
    selectPhase,
    setRecords,
    paginationMsg,
    setPaginationMsg,
  });

  const currentProjectMilestone = milestones?.find(
    (item: any) => item?.value === projectDetailData?.current_milestone,
  );

  // 设置options，加current标识
  const phaseOptions = useMemo(() => {
    let res = [];
    if (Array.isArray(milestones) && currentProjectMilestone?.milestone) {
      milestones?.forEach((t: any) => {
        res.push({
          value: t?.milestone_id,
          label: t?.text,
          text:
            t?.milestone == currentProjectMilestone?.milestone ? (
              <span
                className="gp-project-update-milestone-select-item"
                title={t.text || '-' + ' (current)'}
              >
                <span className="item-project-name">{t.text || '-'}</span>
                <span className="item-project-milestone-current-sign">
                  (current)
                </span>
              </span>
            ) : (
              t?.text
            ),
        });
      });
    } else {
      res = milestones;
    }
    return res;
  }, [milestones, currentProjectMilestone]);

  // 表格使用到的插件
  const addons = [
    pageable({
      // pageIndex: 1,
      // pageSize: 20,
      pageSizeOptions: [20, 50, 100],
      // recordCount,
      stateText: `${getNumberOfDotUnit(paginationMsg?.total)} item${
        paginationMsg?.total > 1 ? 's' : ''
      } in total`,
      onPagingChange: ({ pageIndex, pageSize }) => {
        setPaginationMsg({
          ...paginationMsg,
          pageIndex: pageIndex as number,
          pageSize: pageSize as number,
        });
      },
    }),
    sortable({
      columns: ['due_date'],
      value: sorts ? [sorts] : [],
      onChange: (value) => {
        setDownloadParams?.({
          ...downloadParams,
          sorts: value?.[0],
        });
        setSorts(value?.[0]);
      },
    }),
    scrollable({
      // maxHeight: tableBodyMaxH,
      // minHeight: 200,
    }),
    autotip({
      isLoading: loading,
      hideIcon: !loading,
      emptyText: 'The list has no data yet.',
      style: {
        height: 172,
        display: 'flex',
        width: 'fit-content',
        margin: 'auto',
        textAlign:'center',
      },
    }),
  ];

  if (paginationMsg?.total <= 20) addons.shift();

  const handleStatusFilterChange = useMemoizedFn((v: string[]) => {
    // 重复全选的效果不请求接口
    if (v?.length === ACTION_ITEM_STATUS_OPTIONS?.length && !lastSelectStatus?.length) return;
    // 当length是全选时，也设置成[]
    if (v?.length === ACTION_ITEM_STATUS_OPTIONS?.length) {
      setSelectStatus([]);
      setLastSelectStatus([]);
      setDownloadParams?.({
        ...downloadParams,
        selectStatus: [],
      });
      return;
    }

    const newSelectStatus = v ? [...v] : [];
    setSelectStatus(newSelectStatus);
    setLastSelectStatus(newSelectStatus);
    setDownloadParams?.({
      ...downloadParams,
      selectStatus: newSelectStatus,
    });
  });

  const handlePhaseFilterChange = useMemoizedFn((v: string[]) => {
    // 重复全选的效果不请求接口
    if (v?.length === phaseOptions?.length && !lastSelectPhase?.length) return;
    // 当length是全选时，也设置成[]
    if (v?.length === phaseOptions?.length) {
      setSelectPhase([]);
      setLastSelectPhase([]);
      setDownloadParams?.({
        ...downloadParams,
        selectPhase: [],
      });
      return;
    }

    const newSelectPhase = v ? [...v] : [];
    setSelectPhase(newSelectPhase);
    setLastSelectPhase(newSelectPhase);
    setDownloadParams?.({
      ...downloadParams,
      selectPhase: newSelectPhase,
    });
  });

  // 判断滚动条是否显示，表头样式更改
  const handleResize = useMemoizedFn(() => {
    const tableBody = document.querySelector(
      '.dbg-action-items-list-table .tea-table__body',
    );
    const rightContent = document.querySelector('.tea-layout__content-body');
    // @ts-ignore
    const flag = tableBody?.clientHeight < tableBody?.firstChild?.clientHeight;
    setTableIsScroll(flag);
    // @ts-ignore
    const h =
      paginationMsg?.total <= 20
        ? rightContent?.clientHeight - 232 || 500
        : rightContent?.clientHeight - 278 || 500;
    setTableBodyMaxH(h);
  });

  useEffect(() => {
    if (loading) return;
    setTimeout(() => {
      handleResize();
    });
  }, [
    paginationMsg.total,
    paginationMsg.pageSize,
    paginationMsg.pageIndex,
    loading,
    hideHeader,
    selectStatus,
    selectPhase,
  ]);

  // 处理数据
  useEffect(() => {
    if (loading) {
      setRecords([]);
    } else {
      const list = data?.workplans;
      if (Array.isArray(list)) {
        // 之前的completed处理成现在对应的status
        const orgList = list?.map((item, index) => {
          return {
            ...item,
            uniqueId: index,
            status:
              !item?.status && item?.hasOwnProperty('completed')
                ? item?.completed
                  ? 4
                  : 1
                : item?.status,
          };
        });
        setDataList(orgList);
      }
    }
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <CardSlot className="dbg-action-items-list-container">
      <Table
        // verticalTop
        className="dbg-common-table dbg-action-items-list-table dbg-table-sticky dbh-action-state-center-table"
        records={loading ? [] : records}
        recordKey="uniqueId"
        columns={
          getColumns(
            userIsAbleEdit,
            selectStatus,
            handleStatusFilterChange,
            phaseOptions,
            selectPhase,
            handlePhaseFilterChange,
            onEdit,
          ) as TableProps['columns']
        }
        addons={addons}
      />
    </CardSlot>
  );
}

ActionItemsList.defaultProps = DEFAULT_PROPS;

export default memo(ActionItemsList);
